import { extractTranslations } from '@assets/i18n/config';
import { datadogLogs } from '@datadog/browser-logs';
import { BoundObjectType, FinancialProductStrategy } from '@generated/graphql';

import type {
  HouseholdModelPortfolioChild,
  HouseholdModelPortfolioModel,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import type { HouseholdIpsNarrativeKey } from '../householdIpsComparison/householdIpsFields';
import { baseModelPortfolioId } from '../householdModelPortfolioDraft/entityBoundModel';
import type {
  CreateModelChangeProposalInput,
  ModelChangeAllocationInput,
  ModelChangeAssetClassBranchInput,
  ModelChangeModelDetailsInput,
  TargetAllocationInput,
  UpdateModelChangeProposalInput,
} from './modelChangeProposal.types';

/** The same statement, named one way on the model and another on a proposal. */
const DETAILS_KEY_BY_IPS_FIELD: Record<
  HouseholdIpsNarrativeKey,
  keyof ModelChangeModelDetailsInput
> = {
  translatedName: 'name',
  translatedPortfolioDescription: 'portfolioDescription',
  translatedInvestmentObjective: 'investmentObjective',
  translatedLiquidity: 'liquidity',
  translatedPortfolioDisclaimer: 'portfolioDisclaimer',
};

/**
 * A proposal carries the holdings as the model stores them, one for one:
 * product-service writes them straight back onto the model when the proposal is
 * signed, and checks they still add up to a whole model before it does.
 *
 * A holding with no security behind it cannot be carried and a weightless one
 * is carried at nothing, either of which leaves the proposal short of a whole
 * model. That surfaces much later as a refusal to send it for signature, naming
 * only the total, so the holdings behind it are noted here.
 */
const toAllocations = (
  model: HouseholdModelPortfolioModel,
): ModelChangeAllocationInput[] => {
  const children: HouseholdModelPortfolioChild[] = model.children ?? [];
  const held = children.filter((child) => !!child.financialProduct?.id);
  const weightless = held.filter((child) => child.percentage == null);

  if (held.length < children.length || weightless.length > 0) {
    datadogLogs.logger.warn('Model change proposal holdings incomplete', {
      modelPortfolioId: model.id,
      withoutSecurity: children.length - held.length,
      withoutWeight: weightless.length,
    });
  }

  return held.map((child) => ({
    financialProductId: child.financialProduct?.id as string,
    percentage: child.percentage ?? 0,
    isCashEquivalent: child.isCashEquivalent ?? false,
    ...(child.assetClass?.id ? { assetClassId: child.assetClass.id } : {}),
  }));
};

/** A band the advisor added but never named an asset class for is not a band. */
const toThresholds = (
  model: HouseholdModelPortfolioModel,
): TargetAllocationInput[] | undefined =>
  model.targetAllocations
    ?.filter((allocation) => !!allocation.secondaryAssetClass?.id)
    .map((allocation) => ({
      secondaryAssetClassId: allocation.secondaryAssetClass?.id as string,
      min: allocation.min,
      max: allocation.max,
    }));

/**
 * Group weights, which only asset-class models have. Sending them for any other
 * strategy would describe a model that does not work that way.
 */
const toAssetClassBranches = (
  model: HouseholdModelPortfolioModel,
): ModelChangeAssetClassBranchInput[] | undefined => {
  if (model.strategy !== FinancialProductStrategy.AssetClass) return undefined;

  return model.assetClassBranches
    ?.filter((branch) => !!branch.assetClass?.id)
    .map((branch) => ({
      assetClassId: branch.assetClass.id as string,
      targetPercentage: branch.targetPercentage ?? 0,
    }));
};

/**
 * Translated copy read back from the API carries a `__typename` the input has
 * no field for. A statement the model never had is left out rather than sent
 * empty, so it reads as unchanged instead of as a deletion.
 */
const toModelDetails = (
  model: HouseholdModelPortfolioModel,
  includeModelName: boolean,
): ModelChangeModelDetailsInput | undefined => {
  const details: ModelChangeModelDetailsInput = {};

  for (const [ipsField, detailsKey] of Object.entries(
    DETAILS_KEY_BY_IPS_FIELD,
  ) as [HouseholdIpsNarrativeKey, keyof ModelChangeModelDetailsInput][]) {
    // A name sent here is written onto the household's copy when the proposal
    // is signed. A workflow that never offered the advisor the field has
    // nothing to say about it, so the name is left out.
    if (ipsField === 'translatedName' && !includeModelName) continue;

    const value = model[ipsField];
    if (value) details[detailsKey] = extractTranslations(value);
  }

  return Object.keys(details).length > 0 ? details : undefined;
};

/** The half of a proposal that describes one side of the change. */
const sideOf = (
  model: HouseholdModelPortfolioModel,
  includeModelName: boolean,
) => ({
  allocations: toAllocations(model),
  modelDetails: toModelDetails(model, includeModelName),
  thresholds: toThresholds(model),
  assetClassBranches: toAssetClassBranches(model),
});

export type ModelChangeProposalPayloadArgs = {
  /** The model as the workflow has it, with everything the advisor changed. */
  proposed: HouseholdModelPortfolioModel;
  /** The model as the household had it before the workflow, for the compare view. */
  current?: HouseholdModelPortfolioModel;
  /**
   * Whether the workflow gave the advisor the model name to edit. Off by
   * default, so a workflow that never showed the field leaves the name product
   * -service gave the household's copy alone.
   */
  includeModelName?: boolean;
};

export type CreateModelChangeProposalPayloadArgs =
  ModelChangeProposalPayloadArgs & {
    householdId: string;
    /** Bound entity's owning organization — never `activeOrganization.id`. */
    organizationId: string;
  };

export type UpdateModelChangeProposalPayloadArgs =
  ModelChangeProposalPayloadArgs & { modelChangeProposalId: string };

/**
 * What is being proposed beside what the household has today.
 *
 * Both sides are sent because the proposal is what an approver reads, and it
 * has to be able to show the change without going back to a model that may
 * have moved on since. Without a baseline the current side is left off, which
 * product-service allows while the proposal is a draft and refuses once it is
 * submitted for signature.
 */
export const buildModelChangeProposalPayload = ({
  proposed,
  current,
  includeModelName = false,
}: ModelChangeProposalPayloadArgs) => {
  const proposal = sideOf(proposed, includeModelName);
  const today = current ? sideOf(current, includeModelName) : undefined;

  return {
    proposedAllocations: proposal.allocations,
    ...(today ? { currentAllocations: today.allocations } : {}),
    ...(proposal.modelDetails
      ? { proposedModelDetails: proposal.modelDetails }
      : {}),
    ...(today?.modelDetails ? { currentModelDetails: today.modelDetails } : {}),
    ...(proposal.thresholds
      ? { proposedAssetClassThresholds: proposal.thresholds }
      : {}),
    ...(today?.thresholds
      ? { currentAssetClassThresholds: today.thresholds }
      : {}),
    ...(proposal.assetClassBranches
      ? { proposedAssetClassBranches: proposal.assetClassBranches }
      : {}),
    ...(today?.assetClassBranches
      ? { currentAssetClassBranches: today.assetClassBranches }
      : {}),
  };
};

/**
 * Builds `CreateModelChangeProposalInput`.
 *
 * The proposal names the shelf model rather than the household's copy of it,
 * because product-service is the one that mints or reuses the copy and refuses
 * to be handed one. The household it is scoped to is what tells it which copy.
 * The shelf model is read from what the household has today, since that is the
 * model the change is being made against.
 */
export const buildCreateModelChangeProposalInput = ({
  proposed,
  current,
  householdId,
  organizationId,
  includeModelName,
}: CreateModelChangeProposalPayloadArgs): CreateModelChangeProposalInput => ({
  financialProductId: baseModelPortfolioId(current ?? proposed),
  objectType: BoundObjectType.Household,
  objectId: householdId,
  organizationId,
  // product-service only acts on it when the household's live copy came from
  // another shelf model; a copy of this one is reused as before.
  replaceExistingBase: true,
  ...buildModelChangeProposalPayload({ proposed, current, includeModelName }),
});

/**
 * Builds `UpdateModelChangeProposalInput` for a proposal the workflow already
 * opened. The scope and the model it was raised against are fixed at creation,
 * so only the change itself is sent again.
 */
export const buildUpdateModelChangeProposalInput = ({
  modelChangeProposalId,
  proposed,
  current,
  includeModelName,
}: UpdateModelChangeProposalPayloadArgs): UpdateModelChangeProposalInput => ({
  modelChangeProposalId,
  ...buildModelChangeProposalPayload({ proposed, current, includeModelName }),
});
